import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  createManualTrade,
  fetchManualTradingExchanges,
  fetchManualTradingSymbols,
  previewManualTrade,
} from '../../api/manualTrading';

const COLORS = { ink: '#17323c', muted: '#4f646f', danger: '#9c4f4f', success: '#2f7d5d' };

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatNumber(value) {
  const numeric = toNumber(value);
  return numeric === null ? '—' : numeric.toString();
}

function formatPercent(value) {
  const numeric = toNumber(value);
  return numeric === null ? '—' : `${numeric.toFixed(2)}%`;
}

function formatMoney(value) {
  const numeric = toNumber(value);
  return numeric === null ? '—' : `${numeric.toFixed(2)} USDT`;
}

export default function ManualTradingPage() {
  const [exchanges, setExchanges] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [form, setForm] = useState({
    exchange: '',
    symbol: '',
    direction: 'LONG',
    entryType: 'MARKET',
    amountUsdt: '1000',
    amountType: 'MARGIN',
    leverage: '1',
    entryPrice: '',
    slInitial: '',
    tp1: '',
    tp2: '',
    tp3: '',
  });
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchManualTradingExchanges()
      .then((list) => {
        setExchanges(list || []);
        if (list && list.length) {
          setForm((f) => ({ ...f, exchange: f.exchange || list[0] }));
        }
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!form.exchange) {
      return undefined;
    }
    let active = true;
    fetchManualTradingSymbols(form.exchange)
      .then((list) => {
        if (!active) {
          return;
        }
        setSymbols(list || []);
        setForm((f) => ({ ...f, symbol: (list || []).includes(f.symbol) ? f.symbol : (list || [])[0] || '' }));
      })
      .catch((e) => setError(e.message));
    return () => { active = false; };
  }, [form.exchange]);

  const update = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const requestBody = useCallback(() => ({
    exchange: form.exchange,
    symbol: form.symbol,
    direction: form.direction,
    entryType: form.entryType,
    amountUsdt: toNumber(form.amountUsdt),
    amountType: form.amountType,
    leverage: toNumber(form.leverage),
    entryPrice: toNumber(form.entryPrice),
    slInitial: toNumber(form.slInitial),
    tp1: toNumber(form.tp1),
    tp2: toNumber(form.tp2),
    tp3: toNumber(form.tp3),
  }), [form]);

  const onPreview = async () => {
    setError('');
    setResult(null);
    setBusy(true);
    try {
      setPreview(await previewManualTrade(requestBody()));
    } catch (e) {
      setPreview(null);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    setError('');
    setBusy(true);
    try {
      setResult(await createManualTrade(requestBody(), newIdempotencyKey()));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const simulationBanner = (
    <Alert severity="info" sx={{ mb: 2 }}>
      Simulation only — no order is sent to a real exchange. Real trading stays disabled.
    </Alert>
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.ink }}>Trading manuel (simulation)</Typography>
      <Typography variant="body2" sx={{ color: COLORS.muted, mb: 2 }}>
        Configure a position; the exchange executes; protections (SL/TP) evolve deterministically.
      </Typography>
      {simulationBanner}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        <TextField select label="Exchange" value={form.exchange} onChange={update('exchange')}>
          {exchanges.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField select label="Crypto" value={form.symbol} onChange={update('symbol')}>
          {symbols.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select label="Direction" value={form.direction} onChange={update('direction')}>
          <MenuItem value="LONG">LONG</MenuItem>
          <MenuItem value="SHORT">SHORT</MenuItem>
        </TextField>
        <TextField select label="Type d'entrée" value={form.entryType} onChange={update('entryType')}>
          <MenuItem value="MARKET">MARKET</MenuItem>
          <MenuItem value="LIMIT">LIMIT</MenuItem>
        </TextField>
        <TextField label="Montant USDT" type="number" value={form.amountUsdt} onChange={update('amountUsdt')} />
        <TextField select label="Type de montant" value={form.amountType} onChange={update('amountType')}>
          <MenuItem value="MARGIN">Marge</MenuItem>
          <MenuItem value="NOTIONAL">Notionnel</MenuItem>
        </TextField>
        <TextField label="Levier" type="number" value={form.leverage} onChange={update('leverage')} />
        <TextField label="Prix d'entrée" type="number" value={form.entryPrice} onChange={update('entryPrice')} />
        <TextField label="Stop Loss initial" type="number" value={form.slInitial} onChange={update('slInitial')} />
        <TextField label="TP1 — 40%" type="number" value={form.tp1} onChange={update('tp1')} />
        <TextField label="TP2 — 30%" type="number" value={form.tp2} onChange={update('tp2')} />
        <TextField label="TP3 — 20%" type="number" value={form.tp3} onChange={update('tp3')} />
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={onPreview} disabled={busy}>Simuler</Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={busy || !preview}>
          CRÉER LE TRADE
        </Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

      {preview ? (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Simulation (répartition 40/30/20/10 sur la quantité initiale)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1, mt: 1 }}>
            <Typography variant="body2">Quantité initiale : {formatNumber(preview.initialQty)}</Typography>
            <Typography variant="body2">Notionnel : {formatMoney(preview.notional)} · Marge : {formatMoney(preview.margin)}</Typography>
            <Typography variant="body2">TP1 : {formatNumber(preview.tp1Qty)} ({formatPercent(preview.tp1Percent)}) · gain {formatMoney(preview.tp1GainUsdt)}</Typography>
            <Typography variant="body2">TP2 : {formatNumber(preview.tp2Qty)} ({formatPercent(preview.tp2Percent)}) · gain {formatMoney(preview.tp2GainUsdt)}</Typography>
            <Typography variant="body2">TP3 : {formatNumber(preview.tp3Qty)} ({formatPercent(preview.tp3Percent)}) · gain {formatMoney(preview.tp3GainUsdt)}</Typography>
            <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 700 }}>
              Runner : {formatNumber(preview.runnerQty)} ({formatPercent(preview.runnerPercent)}) — protégé, sans TP fixe
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.danger }}>
              Risque : {formatMoney(preview.riskUsdt)} ({formatPercent(preview.riskPercentOfNotional)} du notionnel)
            </Typography>
            <Typography variant="body2">SL successifs : {formatNumber(preview.slInitial)} → {formatNumber(preview.slAfterTp1)} → {formatNumber(preview.slAfterTp2)} → {formatNumber(preview.slAfterTp3)}</Typography>
          </Box>
          {preview.warnings?.length ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
              {preview.warnings.map((w) => <Chip key={w} size="small" label={w} />)}
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {result ? (
        <Alert severity={result.status === 'ACTIVE' ? 'success' : 'warning'} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Statut : {result.status}</Typography>
          <Typography variant="body2">SL initial : {formatNumber(result.slInitial)} · SL actuel : {formatNumber(result.slCurrent)}</Typography>
          <Typography variant="body2">SL order : {result.exchangeSlOrderId || '—'} · TP1/2/3 : {result.exchangeTp1OrderId || '—'} / {result.exchangeTp2OrderId || '—'} / {result.exchangeTp3OrderId || '—'}</Typography>
          {result.status !== 'ACTIVE'
            ? <Typography variant="body2" sx={{ color: COLORS.danger }}>Incident : protections non confirmées ({result.lastEvent}).</Typography>
            : <Typography variant="body2">Runner réservé : {formatNumber(result.runnerQty)}.</Typography>}
        </Alert>
      ) : null}
    </Paper>
  );
}
